import { lazy, Suspense, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";

import { publicApi } from "@/lib/api";
import { SectionLoading } from "@/components/RichContent";
import NotFound from "@/pages/NotFound";

import Home from "@/pages/Home";
import OurCompany from "@/pages/OurCompany";
import AdvisoryTeam from "@/pages/AdvisoryTeam";
import WhyPirac from "@/pages/WhyPirac";
import Framework from "@/pages/Framework";
import CaseStudies from "@/pages/CaseStudies";
import Testimonials from "@/pages/Testimonials";
import Awards from "@/pages/Awards";
import Blogs from "@/pages/Blogs";
import Contact from "@/pages/Contact";
import PropertyValuation from "@/pages/PropertyValuation";
const CalculatorsHub = lazy(() => import("@/pages/calculators/Hub"));
const Mortgage = lazy(() => import("@/pages/calculators/Mortgage"));
const SalesProceeds = lazy(() => import("@/pages/calculators/SalesProceeds"));
const Purchase = lazy(() => import("@/pages/calculators/Purchase"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsOfUse = lazy(() => import("@/pages/TermsOfUse"));
const Disclaimer = lazy(() => import("@/pages/Disclaimer"));

/**
 * Catch-all route.
 *
 * Page URLs are editable from the dashboard, so the router cannot know every
 * valid path at build time. Anything that does not match a fixed route lands
 * here and is resolved against the server, which is the only authority on
 * whether a path is a renamed page, a redirect, an article, or genuinely gone.
 *
 * This keeps the app a plain SPA while still honouring editable slugs — without
 * it, changing "Our Company" to "/about" would 404 on the client even though the
 * server knows exactly where it went.
 */

const PAGE_COMPONENTS = {
  home: Home,
  our_company: OurCompany,
  advisory_team: AdvisoryTeam,
  why_pirac: WhyPirac,
  framework: Framework,
  case_studies: CaseStudies,
  testimonials: Testimonials,
  awards: Awards,
  calculators: CalculatorsHub,
  calculator_mortgage: Mortgage,
  calculator_sales_proceeds: SalesProceeds,
  calculator_purchase: Purchase,
  blogs: Blogs,
  contact: Contact,
  property_valuation: PropertyValuation,
  privacy_policy: PrivacyPolicy,
  terms_of_use: TermsOfUse,
  disclaimer: Disclaimer,
};

export default function PathResolver() {
  const location = useLocation();
  const navigate = useNavigate();

  const { data, isLoading, isError } = useQuery({
    queryKey: ["public", "resolve", location.pathname],
    queryFn: () => publicApi.resolve(location.pathname),
    // A 404 here is a definitive answer about this URL; retrying cannot change it.
    retry: (count, error) => error.status !== 404 && count < 1,
    staleTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    if (!data) return;
    if (data.type === "redirect" && data.to) {
      navigate(data.to, { replace: true });
    } else if (data.type === "blog") {
      navigate(`/blogs/${data.slug}`, { replace: true });
    } else if (data.type === "caseStudy") {
      navigate(`/case-studies/${data.slug}`, { replace: true });
    }
  }, [data, navigate]);

  if (isLoading) return <SectionLoading label="Loading" />;

  // A resolve failure and a genuine 404 both mean "we cannot show this page".
  if (isError || !data) return <NotFound />;

  if (data.type === "page") {
    const Component = PAGE_COMPONENTS[data.key];
    if (Component)
      return (
        <Suspense fallback={<SectionLoading />}>
          <Component />
        </Suspense>
      );
  }

  // Redirect cases are handled by the effect above; showing a spinner in the
  // gap avoids a flash of the not-found page before navigation happens.
  if (data.type === "redirect" || data.type === "blog" || data.type === "caseStudy") {
    return <SectionLoading label="Redirecting" />;
  }

  return <NotFound />;
}
